import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  ButtonGroup,
  Button,
  Collapse,
  Chip,
  Tabs,
  Tab,
  useTheme,
  alpha
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as BudgetIcon,
  FileDownload as ExportIcon,
  ViewList as DetailIcon,
  Summarize as SummaryIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import regionalDataService from '../../services/regionalDataService';
import SnapshotDataService from '../../services/SnapshotDataService';
// import { formatCurrency } from '../../utils/currencyUtils'; // Removed - using formatNumber instead

/**
 * Transform new snapshot brand summaries to regional format
 * @param {Array} brandSummaries - Brand summaries from new snapshot API
 * @returns {Array} Regional data in expected format
 */
const transformNewSnapshotBrandSummariesToRegional = (brandSummaries) => {
  const regionalData = [];
  
  brandSummaries.forEach(brand => {
    const brandName = brand.brand_name;
    
    brand.campaigns.forEach(campaign => {
      const campaignName = campaign.campaign_name;
      
      campaign.media_types.forEach(mediaType => {
        mediaType.monthly_breakdown.forEach(monthData => {
          const month = monthData.month;
          
          // Create entries for each region based on the regional cost breakdown
          const regionBreakdown = [
            { name: 'NTW', cost: monthData.ntw || 0 },
            { name: 'Central Mid', cost: monthData.central_mid || 0 },
            { name: 'The Rest of Central', cost: monthData.the_rest_of_central || 0 },
            { name: 'GHCM', cost: monthData.ghcm || 0 },
            { name: 'Mekong', cost: monthData.mekong || 0 },
            { name: 'North', cost: monthData.north || 0 }
          ];
          
          regionBreakdown.forEach(region => {
            if (region.cost > 0) { // Only include regions with actual cost
              regionalData.push({
                month: month,
                brand_name: brandName,
                campaign_name: campaignName,
                media_type: mediaType.media_type,
                sub_media_type: mediaType.sub_media_type,
                digital_vendor: mediaType.digital_vendor,
                region: region.name,
                given_cost: region.cost,
                planning_cost: region.cost, // For snapshot, given = planning
                actual_cost: monthData.actual_cost || 0,
                savings: monthData.saving || 0
              });
            }
          });
        });
      });
    });
  });
  
  return regionalData;
};

/**
 * RegionBrandMonthlyTable - Table showing region/brand data by month
 * Features expandable regions, month columns, metric toggle, and sum total column
 */
const RegionBrandMonthlyTable = ({ 
  loading = false, 
  currentYear = null,
  currentMonth = null,
  selectedSnapshotMonth = null, // For snapshot mode
  snapshotData = null, // Pre-loaded snapshot data
  enhancedMetrics = null // Backend financial metrics for consistency
}) => {
  const theme = useTheme();
  const [expandedRegions, setExpandedRegions] = useState(new Set());
  const [regionalData, setRegionalData] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [isSnapshotMode, setIsSnapshotMode] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 = Detail, 1 = Summary
  const [filters, setFilters] = useState({
    brand: 'all',
    year: currentYear || new Date().getFullYear().toString(),
    campaign: 'all'
  });

  // Function to determine if a month should show actual or budget data
  // YTD should be n-2 months (current month - 2)
  const getDataType = (monthString) => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // 1-based month
    const currentYear = currentDate.getFullYear();
    
    // Parse month from string like "2025-01" 
    const [year, month] = monthString.split('-').map(Number);
    
    // Only apply actual/budget logic if it's the current year
    if (year !== currentYear) {
      return 'budget';
    }
    
    // YTD is n-2: If current month is 7, then actual data is for months 1-5
    const actualCutoff = currentMonth - 2;
    return month <= actualCutoff ? 'actual' : 'budget';
  };

  // Get YTD months (n-2 from current month)
  const getYTDMonths = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    const actualCutoff = currentMonth - 2;
    
    return processedData.months.filter(month => {
      const [year, monthNum] = month.split('-').map(Number);
      return year === currentYear && monthNum <= actualCutoff;
    });
  };

  // Calculate YTD totals from actual months only
  const calculateYTDTotal = (data, field) => {
    const ytdMonths = getYTDMonths();
    return ytdMonths.reduce((sum, month) => {
      return sum + (data.months?.[month]?.[field] || 0);
    }, 0);
  };

  // Calculate YTD totals for brand (sum of all regions)
  const calculateBrandYTDTotal = (brand, field) => {
    return brand.regions.reduce((sum, region) => {
      return sum + calculateYTDTotal(region, field);
    }, 0);
  };

  // Calculate YTG (Year-to-Go) = Total Planning - YTD Actual
  // YTG represents the remaining planning from current month onwards
  const calculateYTGTotal = (data) => {
    const totalPlanning = data.yearTotal?.planning || 0;
    const ytdActual = calculateYTDTotal(data, 'actual');
    return Math.max(0, totalPlanning - ytdActual);
  };

  // Calculate YTG for brand (sum of all regions)  
  const calculateBrandYTGTotal = (brand) => {
    return brand.regions.reduce((sum, region) => {
      return sum + calculateYTGTotal(region);
    }, 0);
  };

  // Format number without currency
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Update snapshot mode detection
  useEffect(() => {
    setIsSnapshotMode(!!selectedSnapshotMonth);
  }, [selectedSnapshotMonth]);
  
  // Update filters when currentYear prop changes
  useEffect(() => {
    if (currentYear) {
      setFilters(prev => ({
        ...prev,
        year: currentYear
      }));
    }
  }, [currentYear]);

  // Fetch regional data from API - supports both current and snapshot modes
  useEffect(() => {
    const fetchRegionalData = async () => {
      setApiLoading(true);
      try {
        let response;
        
        if (selectedSnapshotMonth) {
          // Snapshot mode - use SnapshotDataService
          console.log('🏛️ Fetching snapshot regional data for:', selectedSnapshotMonth);
          
          // Try new snapshot structure first
          try {
            response = await SnapshotDataService.getNewSnapshotBrandSummaries(selectedSnapshotMonth);
            if (response?.data?.brand_summaries) {
              console.log('✅ Using new snapshot structure for regional data');
              // Transform new snapshot data to regional format
              const transformedData = transformNewSnapshotBrandSummariesToRegional(response.data.brand_summaries);
              response = { data: { data: transformedData } };
            } else {
              // Fallback to old snapshot structure
              response = await SnapshotDataService.getSnapshotRegionalBreakdown(selectedSnapshotMonth, filters);
            }
          } catch (error) {
            console.warn('⚠️ New snapshot structure failed, falling back to old:', error);
            response = await SnapshotDataService.getSnapshotRegionalBreakdown(selectedSnapshotMonth, filters);
          }
        } else {
          // Current mode - use regular regionalDataService
          console.log('🌐 Fetching current regional data with filters:', filters);
          response = await regionalDataService.getCampaignRegionalBreakdown(filters);
        }
        
        const data = response.data?.data || response.data || [];
        setRegionalData(data);
        
        console.log('✅ Regional data loaded:', {
          mode: selectedSnapshotMonth ? 'snapshot' : 'current',
          snapshotMonth: selectedSnapshotMonth,
          recordCount: data.length,
          filters: filters,
          sampleRecord: data[0] || 'No data'
        });
      } catch (error) {
        console.error('❌ Error loading regional data:', error);
        setRegionalData([]);
      } finally {
        setApiLoading(false);
      }
    };

    fetchRegionalData();
  }, [filters, selectedSnapshotMonth]);

  // Budget configuration - only showing budget data
  const metricConfig = {
    key: 'budget',
    label: 'Budget',
    icon: <BudgetIcon />,
    color: 'primary',
    field: 'given_cost'
  };

  // Process data by region and month from regional API
  const processedData = useMemo(() => {
    if (!regionalData || regionalData.length === 0) return { regions: [], months: [], brands: [] };

    console.log('🔍 Processing regional data for RegionBrandMonthlyTable:', regionalData.length, 'records');

    // Check total planning from raw API data
    const rawTotalPlanning = regionalData.reduce((sum, item) => sum + (parseFloat(item.planning_cost || 0)), 0);
    const rawTotalActual = regionalData.reduce((sum, item) => sum + (parseFloat(item.actual_cost || 0)), 0);
    const rawTotalBudget = regionalData.reduce((sum, item) => sum + (parseFloat(item.given_cost || 0)), 0);
    
    console.log('📊 Raw API Data Totals:', {
      planning: rawTotalPlanning,
      actual: rawTotalActual, 
      budget: rawTotalBudget,
      planningFormatted: formatNumber(rawTotalPlanning),
      message: 'This should match DB total (668B)'
    });

    // Check data by region to see NTW impact
    const regionTotals = {};
    regionalData.forEach(item => {
      const region = item.region || 'Unknown';
      if (!regionTotals[region]) {
        regionTotals[region] = { planning: 0, actual: 0, budget: 0, count: 0 };
      }
      regionTotals[region].planning += parseFloat(item.planning_cost || 0);
      regionTotals[region].actual += parseFloat(item.actual_cost || 0);
      regionTotals[region].budget += parseFloat(item.given_cost || 0);
      regionTotals[region].count += 1;
    });

    console.log('🌍 Data by Region (Raw):', regionTotals);
    
    // Check if NTW data exists and its impact
    const ntwData = regionTotals['NTW'] || regionTotals['ntw'] || null;
    const ntwKeys = Object.keys(regionTotals).filter(key => key.toLowerCase().includes('ntw'));
    if (ntwKeys.length > 0) {
      console.log('⚠️ Found NTW regions that will be filtered:', ntwKeys, regionTotals[ntwKeys[0]]);
    }

    // Extract unique months from data
    const monthsSet = new Set();
    regionalData.forEach(item => {
      if (item.month) {
        monthsSet.add(item.month);
      }
    });
    const months = Array.from(monthsSet).sort();

    // Group data by region and brand
    const regionMap = new Map();
    
    // Also group by brand for Summary view
    const brandMap = new Map();
    
    regionalData.forEach(item => {
      const regionName = item.region || 'Unknown Region';
      const brandName = item.brand_name || 'Unknown Brand';
      const campaignName = item.campaign_name || 'Unknown Campaign';
      const month = item.month || 'Unknown';
      
      // Initialize region if not exists
      if (!regionMap.has(regionName)) {
        regionMap.set(regionName, {
          region: regionName,
          brands: new Map(),
          totals: {},
          yearTotal: { budget: 0, planning: 0, actual: 0 }
        });
      }
      
      const regionData = regionMap.get(regionName);
      
      // Initialize brand if not exists
      if (!regionData.brands.has(brandName)) {
        regionData.brands.set(brandName, {
          brand: brandName,
          campaigns: new Map(),
          months: {},
          yearTotal: { budget: 0, planning: 0, actual: 0 }
        });
      }
      
      const brandData = regionData.brands.get(brandName);
      
      // Initialize campaign if not exists
      if (!brandData.campaigns.has(campaignName)) {
        brandData.campaigns.set(campaignName, {
          campaign: campaignName,
          months: {}
        });
      }
      
      const campaignData = brandData.campaigns.get(campaignName);
      
      // Add monthly data
      const budget = parseFloat(item.given_cost || 0);
      const planning = parseFloat(item.planning_cost || 0);
      const actual = parseFloat(item.actual_cost || 0);
      
      campaignData.months[month] = {
        budget,
        planning,
        actual
      };
      
      // Aggregate brand monthly totals
      if (!brandData.months[month]) {
        brandData.months[month] = { budget: 0, planning: 0, actual: 0 };
      }
      brandData.months[month].budget += budget;
      brandData.months[month].planning += planning;
      brandData.months[month].actual += actual;
      
      // Add to brand year total
      brandData.yearTotal.budget += budget;
      brandData.yearTotal.planning += planning;
      brandData.yearTotal.actual += actual;
      
      // Aggregate region monthly totals
      if (!regionData.totals[month]) {
        regionData.totals[month] = { budget: 0, planning: 0, actual: 0 };
      }
      regionData.totals[month].budget += budget;
      regionData.totals[month].planning += planning;
      regionData.totals[month].actual += actual;
      
      // Add to region year total
      regionData.yearTotal.budget += budget;
      regionData.yearTotal.planning += planning;
      regionData.yearTotal.actual += actual;
      
      // Process brand data for Summary view
      if (!brandMap.has(brandName)) {
        brandMap.set(brandName, {
          brand: brandName,
          regions: new Map(),
          yearTotal: { budget: 0, planning: 0, actual: 0 }
        });
      }
      
      const summaryBrandData = brandMap.get(brandName);
      
      // Initialize region under brand if not exists
      if (!summaryBrandData.regions.has(regionName)) {
        summaryBrandData.regions.set(regionName, {
          region: regionName,
          months: {},
          yearTotal: { budget: 0, planning: 0, actual: 0 }
        });
      }
      
      const brandRegionData = summaryBrandData.regions.get(regionName);
      
      // Add monthly data to brand-region
      if (!brandRegionData.months[month]) {
        brandRegionData.months[month] = { budget: 0, planning: 0, actual: 0 };
      }
      brandRegionData.months[month].budget += budget;
      brandRegionData.months[month].planning += planning;
      brandRegionData.months[month].actual += actual;
      
      // Add to brand-region year total
      brandRegionData.yearTotal.budget += budget;
      brandRegionData.yearTotal.planning += planning;
      brandRegionData.yearTotal.actual += actual;
      
      // Add to brand year total (will be recalculated from regions later)
      summaryBrandData.yearTotal.budget += budget;
      summaryBrandData.yearTotal.planning += planning;
      summaryBrandData.yearTotal.actual += actual;
    });

    // Convert to final format and sort (GHCM -> Central Mid -> The Rest of central -> Mekong -> North)
    // Filter out NTW as it's a sum of other regions
    const regions = Array.from(regionMap.values())
      .map(region => ({
        ...region,
        brands: Array.from(region.brands.values()).map(brand => ({
          ...brand,
          campaigns: Array.from(brand.campaigns.values())
        }))
      }))
      .filter(region => {
        const regionName = region.region.toLowerCase();
        // Filter out NTW as it's sum of other regions
        return !regionName.includes('ntw') && region.brands.length > 0;
      })
      .sort((a, b) => {
        // Define custom sort order (removed NTW)
        const getRegionOrder = (regionName) => {
          const name = regionName.toLowerCase();
          if (name.includes('ghcm') || name.includes('ho chi minh') || name.includes('hcm')) return 1;
          if (name.includes('central mid')) return 2;
          if (name.includes('central') && name.includes('rest')) return 3;
          if (name.includes('mekong')) return 4;
          if (name.includes('north')) return 5;
          return 6; // Other regions at the end
        };
        
        const orderA = getRegionOrder(a.region);
        const orderB = getRegionOrder(b.region);
        
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        
        // If same order, sort alphabetically
        return a.region.localeCompare(b.region);
      });

    // Convert brands to final format for Summary view
    const brands = Array.from(brandMap.values())
      .map(brand => {
        const filteredRegions = Array.from(brand.regions.values())
          .filter(region => {
            const regionName = region.region.toLowerCase();
            // Filter out NTW as it's sum of other regions
            return !regionName.includes('ntw');
          })
          .sort((a, b) => {
            // Same region sorting as above (removed NTW)
            const getRegionOrder = (regionName) => {
              const name = regionName.toLowerCase();
              if (name.includes('ghcm') || name.includes('ho chi minh') || name.includes('hcm')) return 1;
              if (name.includes('central mid')) return 2;
              if (name.includes('central') && name.includes('rest')) return 3;
              if (name.includes('mekong')) return 4;
              if (name.includes('north')) return 5;
              return 6;
            };
            
            const orderA = getRegionOrder(a.region);
            const orderB = getRegionOrder(b.region);
            
            if (orderA !== orderB) {
              return orderA - orderB;
            }
            
            return a.region.localeCompare(b.region);
          });

        // Recalculate brand totals from filtered regions (not from raw aggregation)
        const correctedBrandTotals = {
          budget: filteredRegions.reduce((sum, region) => sum + (region.yearTotal?.budget || 0), 0),
          planning: filteredRegions.reduce((sum, region) => sum + (region.yearTotal?.planning || 0), 0),
          actual: filteredRegions.reduce((sum, region) => sum + (region.yearTotal?.actual || 0), 0)
        };

        return {
          ...brand,
          yearTotal: correctedBrandTotals, // Use corrected totals
          regions: filteredRegions
        };
      })
      .filter(brand => brand.regions.length > 0)
      .sort((a, b) => a.brand.localeCompare(b.brand));

    console.log('✅ Processed regions, brands and months:', { 
      regions: regions.length, 
      brands: brands.length,
      months: months.length,
      sampleRegion: regions[0]?.region,
      sampleBrands: regions[0]?.brands?.length || 0
    });

    // Debug YTD logic
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const actualCutoff = currentMonth - 2;
    console.log('📅 YTD Logic:', {
      currentMonth,
      actualCutoff,
      message: `YTD includes months 1-${actualCutoff} (current month ${currentMonth} - 2)`
    });

    // Debug final totals and compare with raw data
    const finalTotalPlanning = brands.reduce((sum, brand) => sum + (brand.yearTotal?.planning || 0), 0);
    const finalTotalActual = brands.reduce((sum, brand) => sum + (brand.yearTotal?.actual || 0), 0);
    const finalTotalBudget = brands.reduce((sum, brand) => sum + (brand.yearTotal?.budget || 0), 0);
    
    // Also check filtered regions total
    const filteredRegionTotalPlanning = brands.reduce((sum, brand) => 
      sum + brand.regions.reduce((regionSum, region) => 
        regionSum + (region.yearTotal?.planning || 0), 0), 0);
    
    console.log('🔍 Data Loss Analysis:', {
      rawApiData: {
        planning: rawTotalPlanning,
        actual: rawTotalActual,
        budget: rawTotalBudget,
        formatted: `${formatNumber(rawTotalPlanning)} / ${formatNumber(rawTotalActual)} / ${formatNumber(rawTotalBudget)}`
      },
      finalProcessedData: {
        planning: finalTotalPlanning,
        actual: finalTotalActual, 
        budget: finalTotalBudget,
        formatted: `${formatNumber(finalTotalPlanning)} / ${formatNumber(finalTotalActual)} / ${formatNumber(finalTotalBudget)}`
      },
      dataLoss: {
        planning: rawTotalPlanning - finalTotalPlanning,
        actual: rawTotalActual - finalTotalActual,
        budget: rawTotalBudget - finalTotalBudget,
        planningPercent: `${((rawTotalPlanning - finalTotalPlanning) / rawTotalPlanning * 100).toFixed(2)}%`,
        formatted: `Lost: ${formatNumber(rawTotalPlanning - finalTotalPlanning)} planning`
      },
      verification: {
        brandTotalsMatchRegionSum: finalTotalPlanning === filteredRegionTotalPlanning,
        message: finalTotalPlanning !== filteredRegionTotalPlanning ? 
          'Brand totals dont match region sum!' : 'Brand totals OK'
      }
    });

    // Debug individual brand contributions
    console.log('🏢 Brand Contribution Analysis:', 
      brands.map(brand => ({
        brand: brand.brand,
        planning: formatNumber(brand.yearTotal?.planning || 0),
        actual: formatNumber(brand.yearTotal?.actual || 0),
        regions: brand.regions.length
      }))
    );

    return { regions, months, brands };
  }, [regionalData]);

  // Toggle region expansion
  const toggleRegion = (region) => {
    const newExpanded = new Set(expandedRegions);
    if (newExpanded.has(region)) {
      newExpanded.delete(region);
    } else {
      newExpanded.add(region);
    }
    setExpandedRegions(newExpanded);
  };



  // Current metric config - using budget only
  const currentMetric = metricConfig;

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Export function
  const handleExport = () => {
    try {
      // Prepare data for export
      const exportData = [];
      
      if (activeTab === 0) {
        // Detail View Export
        const headerRow = ['Region/Brand', ...processedData.months.map(month => {
          const monthLabel = new Date(month + '-01').toLocaleDateString('en-US', { 
            month: 'short', 
            year: 'numeric' 
          });
          const dataType = getDataType(month) === 'actual' ? 'Actual' : 'Budget';
          return `${monthLabel} (${dataType})`;
        })];
        exportData.push(headerRow);
        
        processedData.regions.forEach(region => {
          const regionRow = [
            `${region.region} (Region)`,
            ...processedData.months.map(month => 
              region.totals[month]?.[getDataType(month)] || 0
            )
          ];
          exportData.push(regionRow);
          
          region.brands.forEach(brand => {
            const brandRow = [
              `  ${brand.brand} (Brand)`,
              ...processedData.months.map(month => 
                brand.months[month]?.[getDataType(month)] || 0
              )
            ];
            exportData.push(brandRow);
          });
          
          exportData.push([]);
        });
      } else {
        // Summary View Export
        const headerRow = [
          'Brand/Region', 
          'YTD Planning', 
          'YTD Actual', 
          '% Actual/Planning',
          'YTG (Planning - Actual)',
          'FY Planning', 
          'Actual YTD + Accrual YTG'
        ];
        exportData.push(headerRow);
        
        // Add Total Row first
        const totalYtdPlanning = processedData.brands.reduce((sum, brand) => sum + calculateBrandYTDTotal(brand, 'planning'), 0);
        const totalYtdActual = processedData.brands.reduce((sum, brand) => sum + calculateBrandYTDTotal(brand, 'actual'), 0);
        const totalYtg = processedData.brands.reduce((sum, brand) => sum + calculateBrandYTGTotal(brand), 0);
        const totalPlanning = processedData.brands.reduce((sum, brand) => sum + (brand.yearTotal?.planning || 0), 0);
        
        const totalRow = [
          'TOTAL',
          totalYtdPlanning,
          totalYtdActual,
          totalYtdPlanning > 0 
            ? `${Math.round(totalYtdActual / totalYtdPlanning * 100)}%`
            : '-',
          totalYtg,
          totalPlanning,
          totalYtdActual + totalYtg
        ];
        exportData.push(totalRow);
        exportData.push([]); // Empty row for spacing
        
        processedData.brands.forEach(brand => {
          const ytdPlanning = calculateBrandYTDTotal(brand, 'planning');
          const ytdActual = calculateBrandYTDTotal(brand, 'actual');
          const ytg = calculateBrandYTGTotal(brand);
          const brandRow = [
            `${brand.brand} (Brand)`,
            ytdPlanning,
            ytdActual,
            ytdPlanning > 0 
              ? `${Math.round(ytdActual / ytdPlanning * 100)}%`
              : '-',
            ytg,
            brand.yearTotal?.planning || 0,
            ytdActual + ytg
          ];
          exportData.push(brandRow);
          
          brand.regions.forEach(region => {
            const regionYtdPlanning = calculateYTDTotal(region, 'planning');
            const regionYtdActual = calculateYTDTotal(region, 'actual');
            const regionYtg = calculateYTGTotal(region);
            const regionRow = [
              `  ${region.region}`,
              regionYtdPlanning,
              regionYtdActual,
              regionYtdPlanning > 0 
                ? `${Math.round(regionYtdActual / regionYtdPlanning * 100)}%`
                : '-',
              regionYtg,
              region.yearTotal?.planning || 0,
              regionYtdActual + regionYtg
            ];
            exportData.push(regionRow);
          });
          
          exportData.push([]);
        });
      }
      
      // Convert to CSV format
      const csvContent = exportData.map(row => 
        row.map(cell => `"${cell}"`).join(',')
      ).join('\n');
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const tabName = activeTab === 0 ? 'detail' : 'summary';
      link.setAttribute('href', url);
      link.setAttribute('download', `region_brand_${tabName}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('✅ Export completed successfully');
    } catch (error) {
      console.error('❌ Export failed:', error);
    }
  };

  if (loading || apiLoading) {
    return (
      <Card elevation={0} sx={{ borderRadius: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Typography>Loading region data...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!processedData.regions.length) {
    return (
      <Card elevation={0} sx={{ borderRadius: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography color="text.secondary">No region data available</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box 
      sx={{ 
        width: '100%',
        minHeight: 'calc(100vh - 200px)',
        display: 'flex',
        flexDirection: 'column',
        p: 1,
        boxSizing: 'border-box'
      }}
    >
      <Card 
        elevation={0} 
        sx={{ 
          borderRadius: 3, 
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          minHeight: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <CardContent sx={{ p: 2, pb: 0, flexShrink: 0 }}>
          {/* Header */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box>
                <Typography variant="h6" sx={{ 
                  fontWeight: 700, 
                  mb: 0.5, 
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  lineHeight: 1.2
                }}>
                  Region & Brand Performance by Month
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  lineHeight: 1.4
                }}>
                  {activeTab === 0 ? 'Expandable view showing budget data by region and brand' : 'Summary view showing total budget data by region'}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ExportIcon />}
                onClick={handleExport}
                disabled={!processedData.regions.length}
                sx={{ 
                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                  textTransform: 'none',
                  minWidth: 100,
                  height: 32,
                  fontWeight: 600
                }}
              >
                Export
              </Button>
            </Box>
            
            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              sx={{
                minHeight: 40,
                '& .MuiTab-root': {
                  minHeight: 40,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  textTransform: 'none',
                  fontWeight: 600,
                  padding: { xs: '6px 8px', sm: '8px 12px' }
                }
              }}
            >
              <Tab
                icon={<DetailIcon />}
                label="Detail"
                iconPosition="start"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
              />
              <Tab
                icon={<SummaryIcon />}
                label="Summary"
                iconPosition="start"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
              />
            </Tabs>
          </Box>
        </CardContent>

        {/* Table Container - Takes remaining space */}
        <Box sx={{ flex: 1, px: 2, pb: 2 }}>
          {activeTab === 0 ? (
            // Detail View - Expandable regions with brands
            <TableContainer 
              component={Paper} 
              elevation={0}
              sx={{ 
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: 2,
                maxHeight: 'calc(100vh - 350px)',
                width: '100%',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: 6,
                  height: 6,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: alpha(theme.palette.grey[300], 0.2),
                  borderRadius: 3,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: alpha(theme.palette.grey[500], 0.3),
                  borderRadius: 3,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.grey[500], 0.5),
                  },
                },
              }}
            >
            <Table size="small" sx={{ 
              width: '100%', 
              minWidth: 800,
              '& .MuiTableCell-root': {
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                py: { xs: 0.5, sm: 0.7 },
                px: { xs: 0.5, sm: 0.7 },
                lineHeight: 1.3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }
            }}>
              <TableHead sx={{ 
                position: 'sticky',
                top: 0,
                zIndex: 12
              }}>
                <TableRow sx={{ 
                  '& .MuiTableCell-head': {
                    backgroundColor: theme => `${theme.palette.primary.main} !important`,
                    color: '#fff !important',
                    fontWeight: 700,
                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                    borderBottom: 'none',
                    py: { xs: 0.75, sm: 1 },
                    px: { xs: 0.5, sm: 0.75 },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 12,
                    backgroundImage: 'none !important',
                    '&:hover': {
                      backgroundColor: theme => `${theme.palette.primary.dark} !important`
                    }
                  },
                  '& .MuiTableCell-stickyHeader': {
                    backgroundColor: theme => `${theme.palette.primary.main} !important`,
                    backgroundImage: 'none !important'
                  }
                }}>
                  <TableCell 
                    sx={{ 
                      minWidth: 160,
                      position: 'sticky',
                      left: 0,
                      top: 0,
                      zIndex: 13,
                      borderRight: `1px solid rgba(255, 255, 255, 0.2)`,
                      backgroundColor: theme => `${theme.palette.primary.main} !important`,
                      backgroundImage: 'none !important'
                    }}
                  >
                    Region / Brand
                  </TableCell>
                  {processedData.months.map((month) => (
                    <TableCell 
                      key={month} 
                      align="right" 
                      sx={{ 
                        minWidth: 80,
                        position: 'sticky',
                        top: 0,
                        zIndex: 12,
                        backgroundColor: theme => `${theme.palette.primary.main} !important`,
                        backgroundImage: 'none !important'
                      }}
                    >
                      <Box>
                        <Typography variant="caption" sx={{ 
                          fontWeight: 'bold', 
                          fontSize: '0.6rem',
                          lineHeight: 1.2,
                          color: 'inherit'
                        }}>
                          {new Date(month + '-01').toLocaleDateString('en-US', { 
                            month: 'short', 
                            year: 'numeric' 
                          })}
                        </Typography>
                        <Typography variant="caption" sx={{ 
                          display: 'block', 
                          fontSize: '0.5rem',
                          lineHeight: 1,
                          color: 'inherit',
                          opacity: 1
                        }}>
                          {getDataType(month) === 'actual' ? 'Actual' : 'Budget'}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            <TableBody>
              {processedData.regions.map((region, regionIndex) => (
                <React.Fragment key={region.region}>
                  {/* Region Row */}
                  <TableRow 
                    hover
                    sx={{ 
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                        cursor: 'pointer'
                      }
                    }}
                    onClick={() => toggleRegion(region.region)}
                  >
                    <TableCell sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      minWidth: 160,
                      padding: '8px 12px',
                      position: 'sticky',
                      left: 0,
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      zIndex: 5,
                      borderRight: `1px solid ${alpha(theme.palette.divider, 0.2)}`
                    }}>
                      <IconButton
                        size="small"
                        sx={{ mr: 1, padding: '2px' }}
                      >
                        {expandedRegions.has(region.region) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ 
                          fontSize: { xs: '0.65rem', sm: '0.7rem' },
                          lineHeight: 1.2,
                          color: 'black'
                        }}>
                          {region.region}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                          <Chip
                            label={`${region.brands.length} brands`}
                            size="small"
                            color="primary"
                            sx={{ 
                              fontSize: { xs: '0.45rem', sm: '0.5rem' },
                              height: 18,
                              fontWeight: 600
                            }}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    {/* Monthly totals for region */}
                    {processedData.months.map(month => (
                      <TableCell key={month} align="right" sx={{ 
                        padding: '8px 12px',
                        minWidth: 100,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        <Typography variant="body2" sx={{ 
                          fontSize: { xs: '0.65rem', sm: '0.7rem' },
                          lineHeight: 1.2,
                          color: 'black',
                          fontWeight: 600
                        }}>
                          {formatNumber(region.totals[month]?.[getDataType(month)])}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>

                  {/* Expandable Brand Rows */}
                  {expandedRegions.has(region.region) && (
                    <React.Fragment>
                      {region.brands.map((brand) => (
                        <TableRow 
                          key={brand.brand}
                          component={motion.tr}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ 
                            duration: 0.3,
                            delay: 0.05,
                            ease: 'easeOut'
                          }}
                          sx={{ 
                            backgroundColor: alpha(theme.palette.grey[50], 0.6),
                            borderLeft: `3px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.grey[100], 0.8)
                            }
                          }}
                        >
                          <TableCell sx={{ 
                            pl: 4, 
                            border: 'none',
                            padding: '8px 12px',
                            minWidth: 160,
                            position: 'sticky',
                            left: 0,
                            backgroundColor: alpha(theme.palette.grey[50], 0.6),
                            zIndex: 4,
                            borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.65rem',
                                lineHeight: 1.2,
                                color: 'black'
                              }}>
                                {brand.brand}
                              </Typography>
                            </Box>
                          </TableCell>
                          
                          {processedData.months.map(month => (
                            <TableCell key={month} align="right" sx={{ 
                              border: 'none',
                              padding: '8px 12px',
                              minWidth: 100,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.65rem',
                                lineHeight: 1.3,
                                fontWeight: 400,
                                color: 'black'
                              }}>
                                {formatNumber(brand.months[month]?.[getDataType(month)])}
                              </Typography>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </React.Fragment>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
            </TableContainer>
          ) : (
            // Summary View - Only region totals
            <TableContainer 
              component={Paper} 
              elevation={0}
              sx={{ 
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                borderRadius: 2,
                maxHeight: 'calc(100vh - 380px)',
                width: '100%',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                  width: 6,
                  height: 6,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: alpha(theme.palette.grey[300], 0.2),
                  borderRadius: 3,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: alpha(theme.palette.grey[500], 0.3),
                  borderRadius: 3,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.grey[500], 0.5),
                  },
                },
              }}
            >
              <Table size="small" sx={{ 
                width: '100%', 
                minWidth: 800,
                '& .MuiTableCell-root': {
                  fontSize: '0.7rem',
                  py: { xs: 0.5, sm: 0.7 },
                  px: { xs: 0.5, sm: 0.7 },
                  lineHeight: 1.4,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}>
                <TableHead sx={{ 
                  position: 'sticky',
                  top: 0,
                  zIndex: 12
                }}>
                  {/* First header row - Group headers */}
                  <TableRow sx={{ 
                    '& .MuiTableCell-head': {
                      backgroundColor: theme => `${theme.palette.primary.main} !important`,
                      color: '#fff !important',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      borderBottom: 'none',
                      py: { xs: 0.75, sm: 1 },
                      px: { xs: 0.5, sm: 0.75 },
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      position: 'sticky',
                      top: 0,
                      zIndex: 12,
                      backgroundImage: 'none !important',
                      '&:hover': {
                        backgroundColor: theme => `${theme.palette.primary.dark} !important`
                      }
                    },
                    '& .MuiTableCell-stickyHeader': {
                      backgroundColor: theme => `${theme.palette.primary.main} !important`,
                      backgroundImage: 'none !important'
                    }
                  }}>
                    <TableCell 
                      sx={{ 
                        minWidth: 160,
                        position: 'sticky',
                        left: 0,
                        top: 0,
                        zIndex: 14,
                        borderRight: `1px solid rgba(255, 255, 255, 0.2)`,
                        backgroundColor: theme => `${theme.palette.primary.main} !important`,
                        backgroundImage: 'none !important'
                      }}
                    >
                      Brand / Region
                    </TableCell>
                    
                    {/* Actual YTD Group */}
                    <TableCell 
                      align="center"
                      colSpan={3}
                      sx={{ 
                        minWidth: 240,
                        position: 'sticky',
                        top: 0,
                        zIndex: 12,
                        backgroundColor: theme => `${alpha(theme.palette.warning.main, 0.9)} !important`,
                        backgroundImage: 'none !important',
                        borderRight: `2px solid rgba(255, 255, 255, 0.3)`,
                      }}
                    >
                      <Typography variant="caption" sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '0.65rem',
                        lineHeight: 1.2,
                        color: 'white'
                      }}>
                        {(() => {
                          const currentDate = new Date();
                          const currentMonth = currentDate.getMonth() + 1;
                          const actualCutoff = currentMonth - 2;
                          return actualCutoff > 0 
                            ? `Actual YTD (Jan - ${new Date(0, actualCutoff - 1).toLocaleString('en-US', { month: 'short' })})`
                            : `Actual YTD`;
                        })()}
                      </Typography>
                    </TableCell>
                    
                    {/* Accrual YTG */}
                    <TableCell 
                      align="center"
                      sx={{ 
                        minWidth: 100,
                        position: 'sticky',
                        top: 0,
                        zIndex: 12,
                        backgroundColor: theme => `${alpha(theme.palette.info.main, 0.9)} !important`,
                        backgroundImage: 'none !important',
                        borderRight: `2px solid rgba(255, 255, 255, 0.3)`,
                      }}
                    >
                      <Typography variant="caption" sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '0.65rem',
                        lineHeight: 1.2,
                        color: 'white'
                      }}>
                        Accrual YTG<br />(Planning - Actual)
                      </Typography>
                    </TableCell>
                    
                    {/* FY 2025 Group */}
                    <TableCell 
                      align="center"
                      colSpan={2}
                      sx={{ 
                        minWidth: 160,
                        position: 'sticky',
                        top: 0,
                        zIndex: 12,
                        backgroundColor: theme => `${alpha(theme.palette.success.main, 0.9)} !important`,
                        backgroundImage: 'none !important'
                      }}
                    >
                      <Typography variant="caption" sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '0.65rem',
                        lineHeight: 1.2,
                        color: 'white'
                      }}>
                        FY 2025
                      </Typography>
                    </TableCell>
                  </TableRow>
                  
                  {/* Second header row with individual columns */}
                  <TableRow sx={{ 
                    '& .MuiTableCell-head': {
                      backgroundColor: theme => `${theme.palette.primary.main} !important`,
                      color: '#fff !important',
                      fontWeight: 700,
                      fontSize: '0.65rem',
                      borderBottom: 'none',
                      py: { xs: 0.5, sm: 0.75 },
                      px: { xs: 0.5, sm: 0.75 },
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                      position: 'sticky',
                      top: '48px', // Position below first header row
                      zIndex: 11,
                      backgroundImage: 'none !important',
                      '&:hover': {
                        backgroundColor: theme => `${theme.palette.primary.dark} !important`
                      }
                    }
                  }}>
                    <TableCell 
                      sx={{ 
                        minWidth: 160,
                        position: 'sticky',
                        left: 0,
                        top: '48px',
                        zIndex: 12,
                        borderRight: `1px solid rgba(255, 255, 255, 0.2)`,
                        backgroundColor: theme => `${theme.palette.primary.main} !important`,
                        backgroundImage: 'none !important'
                      }}
                    >
                      {/* Empty for alignment */}
                    </TableCell>
                    
                    {/* YTD columns */}
                    <TableCell align="right" sx={{ 
                      minWidth: 80,
                      position: 'sticky',
                      top: '48px',
                      zIndex: 11,
                      backgroundColor: theme => `${theme.palette.primary.main} !important`,
                      backgroundImage: 'none !important'
                    }}>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '0.6rem',
                        lineHeight: 1.2,
                        color: 'inherit'
                      }}>
                        Planning
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="right" sx={{ 
                      minWidth: 80,
                      position: 'sticky',
                      top: '48px',
                      zIndex: 11,
                      backgroundColor: theme => `${theme.palette.primary.main} !important`,
                      backgroundImage: 'none !important'
                    }}>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '0.6rem',
                        lineHeight: 1.2,
                        color: 'inherit'
                      }}>
                        Actual
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="right" sx={{ 
                      minWidth: 80,
                      position: 'sticky',
                      top: '48px',
                      zIndex: 11,
                      backgroundColor: theme => `${theme.palette.primary.main} !important`,
                      backgroundImage: 'none !important',
                      borderRight: `2px solid rgba(255, 255, 255, 0.3)`,
                    }}>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '0.6rem',
                        lineHeight: 1.2,
                        color: 'inherit'
                      }}>
                        % Actual / Planning
                      </Typography>
                    </TableCell>
                    
                    {/* YTG column */}
                    <TableCell align="right" sx={{ 
                      minWidth: 100,
                      position: 'sticky',
                      top: '48px',
                      zIndex: 11,
                      backgroundColor: theme => `${theme.palette.primary.main} !important`,
                      backgroundImage: 'none !important',
                      borderRight: `2px solid rgba(255, 255, 255, 0.3)`,
                    }}>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '0.6rem',
                        lineHeight: 1.2,
                        color: 'inherit'
                      }}>
                        YTG
                      </Typography>
                    </TableCell>
                    
                    {/* FY 2025 columns */}
                    <TableCell align="right" sx={{ 
                      minWidth: 80,
                      position: 'sticky',
                      top: '48px',
                      zIndex: 11,
                      backgroundColor: theme => `${theme.palette.primary.main} !important`,
                      backgroundImage: 'none !important'
                    }}>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '0.6rem',
                        lineHeight: 1.2,
                        color: 'inherit'
                      }}>
                        Planning
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="right" sx={{ 
                      minWidth: 100,
                      position: 'sticky',
                      top: '48px',
                      zIndex: 11,
                      backgroundColor: theme => `${theme.palette.primary.main} !important`,
                      backgroundImage: 'none !important'
                    }}>
                      <Typography variant="caption" sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '0.6rem',
                        lineHeight: 1.2,
                        color: 'inherit'
                      }}>
                        Actual YTD + Accrual YTG
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                                 <TableBody>
                   {/* Total Row */}
                   <TableRow 
                     sx={{ 
                       backgroundColor: alpha(theme.palette.error.main, 0.15),
                       borderLeft: `4px solid ${theme.palette.error.main}`,
                       '&:hover': {
                         backgroundColor: alpha(theme.palette.error.main, 0.2)
                       }
                     }}
                   >
                     <TableCell sx={{ 
                       display: 'flex', 
                       alignItems: 'center', 
                       minWidth: 160,
                       padding: '12px 12px',
                       position: 'sticky',
                       left: 0,
                       backgroundColor: alpha(theme.palette.error.main, 0.15),
                       zIndex: 5,
                       borderRight: `1px solid ${alpha(theme.palette.divider, 0.2)}`
                     }}>
                       <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                         <Typography variant="subtitle1" fontWeight="bold" sx={{ 
                           fontSize: '0.8rem',
                           lineHeight: 1.3,
                           color: 'black'
                         }}>
                           TOTAL
                         </Typography>
                         <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                           <Chip
                             label={`${processedData.brands.length} brands`}
                             size="small"
                             color="error"
                             sx={{ 
                               fontSize: '0.5rem',
                               height: 18
                             }}
                           />
                         </Box>
                       </Box>
                     </TableCell>
                     
                     {/* Total YTD Planning */}
                     <TableCell align="right" sx={{ 
                       padding: '12px 12px',
                       minWidth: 80,
                       whiteSpace: 'nowrap',
                       overflow: 'hidden',
                       textOverflow: 'ellipsis'
                     }}>
                       <Typography variant="body2" sx={{ 
                         fontSize: '0.8rem',
                         lineHeight: 1.3,
                         color: 'black',
                         fontWeight: 700
                       }}>
                         {formatNumber(
                           processedData.brands.reduce((sum, brand) => 
                             sum + calculateBrandYTDTotal(brand, 'planning'), 0
                           )
                         )}
                       </Typography>
                     </TableCell>
                     
                     {/* Total YTD Actual */}
                     <TableCell align="right" sx={{ 
                       padding: '12px 12px',
                       minWidth: 80,
                       whiteSpace: 'nowrap',
                       overflow: 'hidden',
                       textOverflow: 'ellipsis'
                     }}>
                       <Typography variant="body2" sx={{ 
                         fontSize: '0.8rem',
                         lineHeight: 1.3,
                         color: 'black',
                         fontWeight: 700
                       }}>
                         {formatNumber(
                           processedData.brands.reduce((sum, brand) => 
                             sum + calculateBrandYTDTotal(brand, 'actual'), 0
                           )
                         )}
                       </Typography>
                     </TableCell>
                     
                     {/* Total % Actual/Planning */}
                     <TableCell align="right" sx={{ 
                       padding: '12px 12px',
                       minWidth: 80,
                       whiteSpace: 'nowrap',
                       overflow: 'hidden',
                       textOverflow: 'ellipsis',
                       borderRight: `2px solid ${alpha(theme.palette.divider, 0.3)}`
                     }}>
                       <Typography variant="body2" sx={{ 
                         fontSize: '0.8rem',
                         lineHeight: 1.3,
                         color: 'black',
                         fontWeight: 700
                       }}>
                         {(() => {
                           const totalYtdPlanning = processedData.brands.reduce((sum, brand) => 
                             sum + calculateBrandYTDTotal(brand, 'planning'), 0);
                           const totalYtdActual = processedData.brands.reduce((sum, brand) => 
                             sum + calculateBrandYTDTotal(brand, 'actual'), 0);
                           return totalYtdPlanning > 0 
                             ? `${Math.round(totalYtdActual / totalYtdPlanning * 100)}%`
                             : '-';
                         })()}
                       </Typography>
                     </TableCell>
                     
                     {/* Total YTG */}
                     <TableCell align="right" sx={{ 
                       padding: '12px 12px',
                       minWidth: 100,
                       whiteSpace: 'nowrap',
                       overflow: 'hidden',
                       textOverflow: 'ellipsis',
                       borderRight: `2px solid ${alpha(theme.palette.divider, 0.3)}`
                     }}>
                       <Typography variant="body2" sx={{ 
                         fontSize: '0.8rem',
                         lineHeight: 1.3,
                         color: 'black',
                         fontWeight: 700
                       }}>
                         {formatNumber(
                           processedData.brands.reduce((sum, brand) => 
                             sum + calculateBrandYTGTotal(brand), 0
                           )
                         )}
                       </Typography>
                     </TableCell>
                     
                     {/* Total FY Planning */}
                     <TableCell align="right" sx={{ 
                       padding: '12px 12px',
                       minWidth: 80,
                       whiteSpace: 'nowrap',
                       overflow: 'hidden',
                       textOverflow: 'ellipsis'
                     }}>
                       <Typography variant="body2" sx={{ 
                         fontSize: '0.8rem',
                         lineHeight: 1.3,
                         color: 'black',
                         fontWeight: 700
                       }}>
                         {formatNumber(
                           processedData.brands.reduce((sum, brand) => 
                             sum + (brand.yearTotal?.planning || 0), 0
                           )
                         )}
                       </Typography>
                     </TableCell>
                     
                     {/* Total Actual YTD + Accrual YTG */}
                     <TableCell align="right" sx={{ 
                       padding: '12px 12px',
                       minWidth: 100,
                       whiteSpace: 'nowrap',
                       overflow: 'hidden',
                       textOverflow: 'ellipsis'
                     }}>
                       <Typography variant="body2" sx={{ 
                         fontSize: '0.8rem',
                         lineHeight: 1.3,
                         color: 'black',
                         fontWeight: 800
                       }}>
                         {formatNumber(
                           processedData.brands.reduce((sum, brand) => 
                             sum + calculateBrandYTDTotal(brand, 'actual') + calculateBrandYTGTotal(brand), 0
                           )
                         )}
                       </Typography>
                     </TableCell>
                   </TableRow>
                   
                   {/* Brand Rows */}
                   {processedData.brands.map((brand, brandIndex) => (
                     <React.Fragment key={brand.brand}>
                       {/* Brand Header Row */}
                       <TableRow 
                         sx={{ 
                           backgroundColor: alpha(theme.palette.info.main, 0.15),
                           borderLeft: `4px solid ${theme.palette.info.main}`,
                           '&:hover': {
                             backgroundColor: alpha(theme.palette.info.main, 0.2)
                           }
                         }}
                       >
                         <TableCell sx={{ 
                           display: 'flex', 
                           alignItems: 'center', 
                           minWidth: 160,
                           padding: '10px 12px',
                           position: 'sticky',
                           left: 0,
                           backgroundColor: alpha(theme.palette.info.main, 0.15),
                           zIndex: 5,
                           borderRight: `1px solid ${alpha(theme.palette.divider, 0.2)}`
                         }}>
                           <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                             <Typography variant="subtitle1" fontWeight="bold" sx={{ 
                               fontSize: '0.75rem',
                               lineHeight: 1.3,
                               color: 'black'
                             }}>
                               {brand.brand}
                             </Typography>
                             <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                               <Chip
                                 label={`${brand.regions.length} regions`}
                                 size="small"
                                 color="info"
                                 sx={{ 
                                   fontSize: '0.5rem',
                                   height: 18
                                 }}
                               />
                             </Box>
                           </Box>
                         </TableCell>
                         {/* YTD Planning */}
                         <TableCell align="right" sx={{ 
                           padding: '10px 12px',
                           minWidth: 80,
                           whiteSpace: 'nowrap',
                           overflow: 'hidden',
                           textOverflow: 'ellipsis'
                         }}>
                           <Typography variant="body2" sx={{ 
                             fontSize: '0.75rem',
                             lineHeight: 1.3,
                             color: 'black',
                             fontWeight: 600
                           }}>
                             {formatNumber(calculateBrandYTDTotal(brand, 'planning'))}
                           </Typography>
                         </TableCell>
                         
                         {/* YTD Actual */}
                         <TableCell align="right" sx={{ 
                           padding: '10px 12px',
                           minWidth: 80,
                           whiteSpace: 'nowrap',
                           overflow: 'hidden',
                           textOverflow: 'ellipsis'
                         }}>
                           <Typography variant="body2" sx={{ 
                             fontSize: '0.75rem',
                             lineHeight: 1.3,
                             color: 'black',
                             fontWeight: 600
                           }}>
                             {formatNumber(calculateBrandYTDTotal(brand, 'actual'))}
                           </Typography>
                         </TableCell>
                         
                         {/* % Actual/Planning */}
                         <TableCell align="right" sx={{ 
                           padding: '10px 12px',
                           minWidth: 80,
                           whiteSpace: 'nowrap',
                           overflow: 'hidden',
                           textOverflow: 'ellipsis',
                           borderRight: `2px solid ${alpha(theme.palette.divider, 0.3)}`
                         }}>
                           <Typography variant="body2" sx={{ 
                             fontSize: '0.75rem',
                             lineHeight: 1.3,
                             color: 'black',
                             fontWeight: 600
                           }}>
                             {(() => {
                               const ytdPlanning = calculateBrandYTDTotal(brand, 'planning');
                               const ytdActual = calculateBrandYTDTotal(brand, 'actual');
                               return ytdPlanning > 0 
                                 ? `${Math.round(ytdActual / ytdPlanning * 100)}%`
                                 : '-';
                             })()}
                           </Typography>
                         </TableCell>
                         
                         {/* YTG Planning */}
                         <TableCell align="right" sx={{ 
                           padding: '10px 12px',
                           minWidth: 100,
                           whiteSpace: 'nowrap',
                           overflow: 'hidden',
                           textOverflow: 'ellipsis',
                           borderRight: `2px solid ${alpha(theme.palette.divider, 0.3)}`
                         }}>
                           <Typography variant="body2" sx={{ 
                             fontSize: '0.75rem',
                             lineHeight: 1.3,
                             color: 'black',
                             fontWeight: 600
                           }}>
                             {formatNumber(calculateBrandYTGTotal(brand))}
                           </Typography>
                         </TableCell>
                         
                         {/* FY Planning */}
                         <TableCell align="right" sx={{ 
                           padding: '10px 12px',
                           minWidth: 80,
                           whiteSpace: 'nowrap',
                           overflow: 'hidden',
                           textOverflow: 'ellipsis'
                         }}>
                           <Typography variant="body2" sx={{ 
                             fontSize: '0.75rem',
                             lineHeight: 1.3,
                             color: 'black',
                             fontWeight: 600
                           }}>
                             {formatNumber(brand.yearTotal?.planning || 0)}
                           </Typography>
                         </TableCell>
                         
                         {/* Actual YTD + Accrual YTG */}
                         <TableCell align="right" sx={{ 
                           padding: '10px 12px',
                           minWidth: 100,
                           whiteSpace: 'nowrap',
                           overflow: 'hidden',
                           textOverflow: 'ellipsis'
                         }}>
                           <Typography variant="body2" sx={{ 
                             fontSize: '0.75rem',
                             lineHeight: 1.3,
                             color: 'black',
                             fontWeight: 700
                           }}>
                             {formatNumber(calculateBrandYTDTotal(brand, 'actual') + calculateBrandYTGTotal(brand))}
                           </Typography>
                         </TableCell>
                       </TableRow>
                       
                       {/* Region Rows under Brand */}
                       {brand.regions.map((region) => (
                         <TableRow 
                           key={`${brand.brand}-${region.region}`}
                           hover
                           sx={{ 
                             backgroundColor: alpha(theme.palette.grey[50], 0.4),
                             borderLeft: `2px solid ${alpha(theme.palette.grey[400], 0.5)}`,
                             '&:hover': {
                               backgroundColor: alpha(theme.palette.grey[100], 0.6)
                             }
                           }}
                         >
                           <TableCell sx={{ 
                             pl: 4,
                             minWidth: 160,
                             padding: '6px 12px 6px 24px',
                             position: 'sticky',
                             left: 0,
                             backgroundColor: alpha(theme.palette.grey[50], 0.4),
                             zIndex: 4,
                             borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                           }}>
                             <Typography variant="body2" sx={{ 
                               fontSize: '0.65rem',
                               lineHeight: 1.2,
                               color: 'black',
                               fontWeight: 400
                             }}>
                               {region.region}
                             </Typography>
                           </TableCell>
                                                       {/* YTD Planning */}
                            <TableCell align="right" sx={{ 
                              padding: '6px 12px',
                              minWidth: 80,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.65rem',
                                lineHeight: 1.3,
                                color: 'black',
                                fontWeight: 400
                              }}>
                                {formatNumber(calculateYTDTotal(region, 'planning'))}
                              </Typography>
                            </TableCell>
                            
                            {/* YTD Actual */}
                            <TableCell align="right" sx={{ 
                              padding: '6px 12px',
                              minWidth: 80,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.65rem',
                                lineHeight: 1.3,
                                color: 'black',
                                fontWeight: 400
                              }}>
                                {formatNumber(calculateYTDTotal(region, 'actual'))}
                              </Typography>
                            </TableCell>
                            
                            {/* % Actual/Planning */}
                            <TableCell align="right" sx={{ 
                              padding: '6px 12px',
                              minWidth: 80,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              borderRight: `2px solid ${alpha(theme.palette.divider, 0.2)}`
                            }}>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.65rem',
                                lineHeight: 1.3,
                                color: 'black',
                                fontWeight: 400
                              }}>
                                {(() => {
                                  const ytdPlanning = calculateYTDTotal(region, 'planning');
                                  const ytdActual = calculateYTDTotal(region, 'actual');
                                  return ytdPlanning > 0 
                                    ? `${Math.round(ytdActual / ytdPlanning * 100)}%`
                                    : '-';
                                })()}
                              </Typography>
                            </TableCell>
                            
                            {/* YTG Planning */}
                            <TableCell align="right" sx={{ 
                              padding: '6px 12px',
                              minWidth: 100,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              borderRight: `2px solid ${alpha(theme.palette.divider, 0.2)}`
                            }}>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.65rem',
                                lineHeight: 1.3,
                                color: 'black',
                                fontWeight: 400
                              }}>
                                {formatNumber(calculateYTGTotal(region))}
                              </Typography>
                            </TableCell>
                            
                            {/* FY Planning */}
                            <TableCell align="right" sx={{ 
                              padding: '6px 12px',
                              minWidth: 80,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.65rem',
                                lineHeight: 1.3,
                                color: 'black',
                                fontWeight: 400
                              }}>
                                {formatNumber(region.yearTotal?.planning || 0)}
                              </Typography>
                            </TableCell>
                            
                            {/* Actual YTD + Accrual YTG */}
                            <TableCell align="right" sx={{ 
                              padding: '6px 12px',
                              minWidth: 100,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.65rem',
                                lineHeight: 1.3,
                                color: 'black',
                                fontWeight: 500
                              }}>
                                {formatNumber(calculateYTDTotal(region, 'actual') + calculateYTGTotal(region))}
                              </Typography>
                            </TableCell>
                         </TableRow>
                       ))}
                     </React.Fragment>
                   ))}
                 </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Summary Stats */}
        <CardContent sx={{ pt: 1, pb: 2, px: 2, flexShrink: 0 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {activeTab === 0 
                ? `Showing ${processedData.regions.length} regions • ${processedData.months.length} months`
                : `Showing ${processedData.brands.length} brands • ${processedData.regions.length} regions • ${processedData.months.length} months`
              }
            </Typography>
            <Chip
              icon={currentMetric.icon}
              label={`${currentMetric.label}`}
              color={currentMetric.color}
              variant="outlined"
              size="small"
              sx={{ fontSize: '0.7rem' }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RegionBrandMonthlyTable;